import { useMemo, useState } from "react"

import { clsx } from "clsx"

import { ProjectionChart } from "./projection-chart"
import {
  Eyebrow,
  GradText,
  Panel,
  Section,
  SectionHeading,
} from "../../BatteryOnlyProposal/primitives"
import { SavingsDial } from "../../BatteryOnlyProposal/SavingsDial"
import {
  formatUsd,
  formatUsdExact,
} from "../../BatteryOnlyProposal/view-helpers"
import {
  STALE_FIGURES_NOTE,
  STALE_FIGURES_WITHIN_CLASS,
} from "../../ProposalPresentation/updates/stale-figure"
import type {
  SolarDialYear,
  SolarNetSavingsView,
  SolarSavings,
  SolarUtilitySavings,
} from "../solar-presentation"

function MonthlyProduction({
  months,
}: {
  months: SolarSavings[`monthlyProduction`]
}): JSX.Element {
  const peak = Math.max(...months.map((month) => month.kwh), 1)

  return (
    <div className="flex w-full items-end justify-between gap-1.5 sm:gap-2">
      {months.map((month) => (
        <div
          key={month.month}
          className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
        >
          <span className="text-[9.5px] text-bp-text-dim tabular-nums">
            {month.kwh.toLocaleString(`en-US`)}
          </span>
          <div className="relative h-[120px] w-full max-w-[22px] overflow-hidden rounded-md border border-bp-border bg-bp-glass">
            <div
              className="absolute bottom-0 w-full rounded-b-[5px] bg-[image:linear-gradient(180deg,var(--bp-accent),color-mix(in_srgb,var(--bp-accent)_30%,transparent))]"
              style={{ height: `${(month.kwh / peak) * 100}%` }}
            />
          </div>
          <span className="text-[9.5px] font-semibold tracking-[0.1em] text-bp-text-dim uppercase">
            {month.month}
          </span>
        </div>
      ))}
    </div>
  )
}

function Stat({
  label,
  value,
  suffix,
  note,
  details = [],
  gradient = false,
}: {
  label: string
  value: string
  suffix?: string
  note: string
  /** Secondary figures under the note, one per line. */
  details?: string[]
  gradient?: boolean
}): JSX.Element {
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <div
        className={`font-display mt-1.5 text-[length:var(--step-4)] leading-none font-semibold tracking-[-0.025em] tabular-nums ${
          gradient
            ? `bg-[image:var(--bp-grad-accent)] bg-clip-text text-transparent`
            : `text-bp-text`
        }`}
      >
        {value}
        {suffix ? (
          <span className="ml-1 text-lg font-medium text-bp-text-muted">
            {suffix}
          </span>
        ) : null}
      </div>
      <div className="mt-1 text-[11.5px] text-bp-text-muted">{note}</div>
      {details.map((detail) => (
        <div
          key={detail}
          className="mt-1.5 text-[11.5px] text-bp-text-dim tabular-nums"
        >
          {detail}
        </div>
      ))}
    </div>
  )
}

export function Savings({
  savings,
}: {
  savings: SolarSavings
}): JSX.Element | null {
  const byBill = useMemo(
    () =>
      new Map(
        savings.scenarios.map((scenario) => [scenario.monthlyBill, scenario]),
      ),
    [savings.scenarios],
  )
  const bills = savings.scenarios.map((scenario) => scenario.monthlyBill)
  const minBill = Math.min(...bills)
  const maxBill = Math.max(...bills)
  const step = bills.length > 1 ? (bills[1] ?? minBill) - minBill : 1
  const [monthlyBill, setMonthlyBill] = useState(savings.defaultMonthlyBill)
  const scenario = byBill.get(monthlyBill)
  if (!scenario) {
    return null
  }
  const { utilityName, termYears } = savings
  const breakEven =
    scenario.breakEvenYear == null ? `—` : String(scenario.breakEvenYear)

  return (
    <Section id="savings">
      <SectionHeading
        eyebrow="The math"
        sub={`Drag the dial to match your real ${utilityName} bill. Utility rates rise ~${savings.escalatorPct}% every year — solar locks your energy cost in place. The gap between those two lines is your savings.`}
      >
        A line that <GradText>bends in your favor</GradText>.
      </SectionHeading>

      <Panel className="mb-3.5 grid grid-cols-1 items-center gap-12 rounded-[20px] p-6 lg:grid-cols-[auto_1fr] lg:p-8">
        <div className="flex flex-col items-center gap-3.5">
          <SavingsDial
            value={monthlyBill}
            min={minBill}
            max={maxBill}
            step={step}
            size={320}
            onChange={setMonthlyBill}
            label="Your monthly utility bill"
            valueText={`${formatUsd(monthlyBill)} per month`}
          >
            <div>
              <Eyebrow className="mb-1.5">Your bill today</Eyebrow>
              <div className="font-display bg-[image:var(--bp-grad-accent)] bg-clip-text text-[56px] leading-none font-semibold tracking-[-0.03em] text-transparent tabular-nums">
                {formatUsd(monthlyBill)}
              </div>
              <div className="mt-1.5 text-[13px] text-bp-text-muted">
                per month
              </div>
            </div>
          </SavingsDial>
          <button
            type="button"
            onClick={() => setMonthlyBill(savings.defaultMonthlyBill)}
            className="text-[11.5px] text-bp-text-muted underline underline-offset-[3px] hover:text-bp-text"
          >
            Reset to {formatUsd(savings.defaultMonthlyBill)}
          </button>
        </div>

        <div className="grid gap-6">
          <div>
            <Eyebrow className="mb-2">If you do nothing</Eyebrow>
            <p className="font-display text-[length:var(--step-1)] font-medium tracking-[-0.01em] text-bp-text-soft">
              You&apos;ll pay{` `}
              <span className="font-semibold text-bp-danger">
                {formatUsd(scenario.totalUtility)}
              </span>
              {` `}to your utility over {termYears} years.
            </p>
          </div>
          <div className="grid gap-6 rounded-lg border border-bp-border bg-bp-elevated px-7 py-[26px] md:grid-cols-3 md:gap-0 md:divide-x md:divide-bp-border [&>*]:md:px-6 [&>*:first-child]:md:pl-0 [&>*:last-child]:md:pr-0">
            <Stat
              label="Go solar — you save"
              value={formatUsd(scenario.totalSavings)}
              note={`over ${termYears} years`}
              gradient
            />
            <Stat
              label="Break-even"
              value={breakEven}
              suffix="yr"
              note="system pays for itself"
            />
            <Stat
              label="Monthly payment"
              value={formatUsd(savings.solarMonthlyPayment)}
              suffix="/mo"
              note={`locked for ${termYears} years`}
            />
          </div>
        </div>
      </Panel>

      <Panel className="rounded-[20px] p-6 lg:p-8">
        <div className="mb-6 flex flex-wrap items-start gap-x-10 gap-y-4">
          <div>
            <Eyebrow>{termYears}-year utility cost (projected)</Eyebrow>
            <div className="font-display mt-1 text-[length:var(--step-3)] font-semibold tracking-[-0.02em] text-bp-danger tabular-nums">
              {formatUsd(scenario.totalUtility)}
            </div>
          </div>
          <div>
            <Eyebrow>{termYears}-year solar cost (locked)</Eyebrow>
            <div className="font-display mt-1 text-[length:var(--step-3)] font-semibold tracking-[-0.02em] tabular-nums">
              {formatUsd(scenario.totalSolar)}
            </div>
          </div>
          <div className="flex-1" />
          <div className="text-right">
            <Eyebrow className="text-bp-success">Your savings</Eyebrow>
            <div className="font-display mt-1 text-[length:var(--step-3)] font-semibold tracking-[-0.02em] text-bp-success tabular-nums">
              {formatUsd(scenario.totalSavings)}
            </div>
          </div>
        </div>
        <ProjectionChart
          years={scenario.years}
          breakEvenYear={scenario.breakEvenYear}
          label={`Cumulative cost over ${scenario.years.length} years: ${formatUsd(scenario.totalUtility)} staying with the utility, ${formatUsd(scenario.totalSolar)} going solar.`}
        />
        <div className="mt-3.5 flex flex-wrap gap-6 text-[12.5px] text-bp-text-muted">
          <span className="inline-flex items-center gap-2">
            <span className="h-0.5 w-3.5 bg-bp-danger" /> Staying with{` `}
            {utilityName}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-0.5 w-3.5 bg-[image:repeating-linear-gradient(90deg,var(--bp-text-soft)_0_4px,transparent_4px_7px)]" />
            Going solar
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-2 w-3.5 bg-bp-success/30" /> Cumulative savings
          </span>
        </div>
      </Panel>

      <Panel className="mt-3.5 px-7 py-[22px]">
        <Eyebrow className="mb-4">Monthly production · kWh</Eyebrow>
        <MonthlyProduction months={savings.monthlyProduction} />
      </Panel>
    </Section>
  )
}

function YearDial({
  year,
  termYears,
  rate,
  onChange,
}: {
  year: number
  termYears: number
  rate: number
  onChange: (next: number) => void
}): JSX.Element {
  return (
    <SavingsDial
      value={year}
      min={1}
      max={termYears}
      step={1}
      size={320}
      onChange={onChange}
      label="Savings year"
      valueText={`Year ${year}, utility rate ${formatUsdExact(rate)} per kWh`}
    >
      <div>
        <div className="font-display bg-[image:var(--bp-grad-accent)] bg-clip-text text-[64px] leading-none font-semibold tracking-[-0.03em] text-transparent tabular-nums">
          {year}
        </div>
        <div className="mt-1 text-[13px] text-bp-text-muted">
          {year === 1 ? `year` : `years`}
        </div>
        <div className="mt-2 text-[15px] font-semibold text-bp-text-soft tabular-nums">
          {formatUsdExact(rate)}/kWh
        </div>
      </div>
    </SavingsDial>
  )
}

/**
 * The selected year's figures, beside the dial: the bills that year, the
 * system's payment and the net when it's known, and what's been saved by then.
 */
function SelectedYear({
  dialYear,
  utility,
  net,
}: {
  dialYear: SolarDialYear
  utility: string
  net: SolarNetSavingsView | null
}): JSX.Element {
  const { year } = dialYear
  const yearNet = net && dialYear.net
  const saved = yearNet
    ? yearNet.cumulativeNetSavings
    : dialYear.cumulativeSavings

  return (
    <div
      data-testid="solar-savings-selected-year"
      className="grid gap-6 rounded-lg border border-bp-border bg-bp-elevated px-7 py-[26px] md:grid-cols-3 md:gap-0 md:divide-x md:divide-bp-border [&>*]:md:px-6 [&>*:first-child]:md:pl-0 [&>*:last-child]:md:pr-0"
    >
      <Stat
        label={`Your ${utility} bill in year ${year}`}
        value={formatUsd(dialYear.utilityMonthly)}
        suffix="/mo"
        note="without solar"
      />
      <Stat
        label="With solar"
        value={formatUsd(dialYear.withSolarUtilityMonthly)}
        suffix="/mo"
        note="utility bill"
        details={
          net && yearNet
            ? [
                `${formatUsd(yearNet.monthlyPayment)}/mo ${net.paymentLabel}`,
                `${formatUsd(yearNet.monthlyNetSavings)}/mo net savings`,
              ]
            : []
        }
      />
      <Stat
        label={`Saved by year ${year}`}
        value={formatUsd(saved)}
        note={net && yearNet ? net.basisLabel : `on utility bills`}
        details={
          yearNet
            ? [
                `${formatUsd(dialYear.cumulativeSavings)} in utility-bill savings`,
              ]
            : []
        }
        gradient={saved >= 0}
      />
    </div>
  )
}

/**
 * "The math" for a real proposal: a year dial over the stored years, and
 * savings net of the system's cost with their break-even, or utility-only
 * savings when that cost isn't known. Every figure is the server's; moving the
 * dial picks a year and fetches nothing. Utility-bill savings stay visible
 * beside a net figure so the two aren't confused.
 */
export function UtilitySavings({
  savings,
  stale = false,
  initialYear,
}: {
  savings: SolarUtilitySavings
  /** Seller page only: underline the savings figures as out of date. */
  stale?: boolean
  /**
   * The year the dial opens on; a print sets it, a reader drags from there.
   * Unset, it opens on break-even, or year 1 without one.
   */
  initialYear?: number
}): JSX.Element {
  const { termYears, net, dialYears } = savings
  const utility = savings.utilityName ?? `your utility`
  // With a compute node, the totals and the line add its guaranteed base (never
  // the bonus); the bill figures and the dial stay bill-only, exactly as returned.
  const compute = net?.compute ?? null
  const savedTotal = compute
    ? compute.netSavings
    : net
      ? net.netSavings
      : savings.totalSavings
  const breakEvenYear = compute
    ? compute.breakEvenYear
    : (net?.breakEvenYear ?? null)
  const totalWithSolar = compute
    ? compute.totalWithSolar
    : net
      ? net.totalWithSolar
      : savings.totalWithSolar
  // The compute base can outrun the bill and the system's cost; that total then
  // reads as what's left over, never as a negative cost.
  const computeSurplus = compute != null && totalWithSolar < 0
  const [selectedYear, setSelectedYear] = useState(() =>
    Math.max(1, initialYear ?? breakEvenYear ?? 1),
  )
  // A recalculation can shorten the years; the dial then holds the last one.
  const dialYear =
    dialYears[Math.min(selectedYear, dialYears.length) - 1] ?? dialYears[0]

  return (
    <Section id="savings">
      <SectionHeading
        eyebrow="The math"
        sub={`Your ${savings.utilityName ?? `utility`} bill is about ${formatUsd(savings.billToday)}/mo today${
          savings.escalatorPct > 0
            ? `, and rates rise ~${savings.escalatorPct}% a year`
            : ``
        }. Solar cuts what you buy from the grid; the gap between the two lines is your savings${
          net ? ` after the system's cost` : ``
        }. Drag the dial to see any year.`}
      >
        A line that <GradText>bends in your favor</GradText>.
      </SectionHeading>

      <Panel className="mb-3.5 grid grid-cols-1 items-center gap-12 rounded-[20px] p-6 lg:grid-cols-[auto_1fr] lg:p-8">
        {dialYear ? (
          <div className="flex flex-col items-center gap-3.5">
            <YearDial
              year={dialYear.year}
              termYears={dialYears.length}
              rate={dialYear.utilityRatePerKwh}
              onChange={setSelectedYear}
            />
            <button
              type="button"
              onClick={() => setSelectedYear(1)}
              className="min-h-6 px-2 text-[11.5px] text-bp-text-muted underline underline-offset-[3px] hover:text-bp-text"
            >
              Back to year 1
            </button>
          </div>
        ) : null}

        <div className="grid gap-6">
          <div>
            <Eyebrow className="mb-2">If you do nothing</Eyebrow>
            <p className="font-display text-[length:var(--step-1)] font-medium tracking-[-0.01em] text-bp-text-soft">
              You&apos;ll pay{` `}
              <span className="font-semibold text-bp-danger">
                {formatUsd(savings.totalUtility)}
              </span>
              {` `}to {utility} over {termYears} years.
            </p>
          </div>
          {stale ? <p className="sr-only">{STALE_FIGURES_NOTE}</p> : null}
          {dialYear ? (
            <div className={stale ? STALE_FIGURES_WITHIN_CLASS : undefined}>
              <SelectedYear dialYear={dialYear} utility={utility} net={net} />
            </div>
          ) : null}
        </div>
      </Panel>

      <Panel className="rounded-[20px] p-6 lg:p-8">
        <div
          data-testid={stale ? `solar-savings-stale` : undefined}
          className={clsx(
            `mb-6 grid gap-6`,
            stale && STALE_FIGURES_WITHIN_CLASS,
          )}
        >
          <div data-testid="solar-savings-total">
            <Eyebrow
              className={savedTotal < 0 ? `text-bp-danger` : `text-bp-success`}
            >
              {compute ? `Solar + compute, you save` : `Your savings`}
            </Eyebrow>
            <div
              className={`font-display mt-1.5 text-[length:var(--step-4)] leading-none font-semibold tracking-[-0.025em] tabular-nums ${
                savedTotal < 0 ? `text-bp-danger` : `text-bp-success`
              }`}
            >
              {formatUsd(savedTotal)}
            </div>
            <div className="mt-2 text-[12.5px] text-bp-text-muted">
              {compute
                ? `over ${termYears} years, ${net?.basisLabel ?? ``}, with ${compute.incomeYears} years of the guaranteed compute node base`
                : net
                  ? `over ${termYears} years, ${net.basisLabel}`
                  : `over ${termYears} years`}
            </div>
            {net ? (
              <div className="mt-1 text-[12.5px] text-bp-text-dim tabular-nums">
                {formatUsd(savings.totalSavings)} in utility-bill savings
              </div>
            ) : null}
          </div>
          {/* The breakdown sits bottom-aligned so the figures share a line even
              when a long label wraps. */}
          <div className="grid gap-6 border-t border-bp-border pt-6 sm:grid-flow-col sm:auto-cols-fr sm:gap-0 sm:divide-x sm:divide-bp-border [&>*]:flex [&>*]:flex-col [&>*]:justify-end [&>*]:sm:px-6 [&>*:first-child]:sm:pl-0 [&>*:last-child]:sm:pr-0">
            <div>
              <Eyebrow>{termYears}-year utility cost (projected)</Eyebrow>
              <div className="font-display mt-1 text-[length:var(--step-3)] font-semibold tracking-[-0.02em] text-bp-danger tabular-nums">
                {formatUsd(savings.totalUtility)}
              </div>
            </div>
            <div>
              <Eyebrow>
                {computeSurplus
                  ? `${termYears}-year compute base beyond costs going solar`
                  : compute
                    ? `${termYears}-year cost going solar, less the compute base`
                    : net
                      ? `${termYears}-year cost going solar`
                      : `${termYears}-year utility cost with solar`}
              </Eyebrow>
              <div className="font-display mt-1 text-[length:var(--step-3)] font-semibold tracking-[-0.02em] tabular-nums">
                {formatUsd(computeSurplus ? -totalWithSolar : totalWithSolar)}
              </div>
            </div>
            {net ? (
              <div data-testid="solar-savings-break-even">
                <Eyebrow>Break-even</Eyebrow>
                <div className="font-display mt-1 text-[length:var(--step-3)] font-semibold tracking-[-0.02em] tabular-nums">
                  {breakEvenYear == null
                    ? `Not within ${termYears} years`
                    : `Year ${breakEvenYear}`}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <ProjectionChart
          years={compute ? compute.years : net ? net.years : savings.years}
          breakEvenYear={net ? breakEvenYear : null}
          selectedYear={dialYear?.year ?? null}
          label={
            compute
              ? computeSurplus
                ? `Cumulative cost over ${termYears} years: ${formatUsd(savings.totalUtility)} staying with ${utility}. Going solar, ${compute.incomeYears} years of the guaranteed compute base exceed the utility bill and system cost by ${formatUsd(-compute.totalWithSolar)}.`
                : `Cumulative cost over ${termYears} years: ${formatUsd(savings.totalUtility)} staying with ${utility}, ${formatUsd(compute.totalWithSolar)} going solar, utility bill and system cost less ${compute.incomeYears} years of the guaranteed compute base.`
              : net
                ? `Cumulative cost over ${termYears} years: ${formatUsd(savings.totalUtility)} staying with ${utility}, ${formatUsd(net.totalWithSolar)} going solar, utility bill and system cost.`
                : `Cumulative utility cost over ${termYears} years: ${formatUsd(savings.totalUtility)} staying with ${utility}, ${formatUsd(savings.totalWithSolar)} with solar.`
          }
        />
        <div className="mt-3.5 flex flex-wrap gap-6 text-[12.5px] text-bp-text-muted">
          <span className="inline-flex items-center gap-2">
            <span className="h-0.5 w-3.5 bg-bp-danger" /> Staying with{` `}
            {utility}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-0.5 w-3.5 bg-[image:repeating-linear-gradient(90deg,var(--bp-text-soft)_0_4px,transparent_4px_7px)]" />
            {compute
              ? `Going solar (utility bill + system cost − compute base)`
              : net
                ? `Going solar (utility bill + system cost)`
                : `Utility bill with solar`}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-2 w-3.5 bg-bp-success/30" /> Cumulative savings
          </span>
        </div>
      </Panel>

      {savings.monthlyProduction.length > 0 ? (
        <Panel className="mt-3.5 px-7 py-[22px]">
          <Eyebrow className="mb-4">Monthly production · kWh</Eyebrow>
          <MonthlyProduction months={savings.monthlyProduction} />
        </Panel>
      ) : null}
    </Section>
  )
}
